import * as SunCalc from 'suncalc'
import type { HourPoint, Place } from '../../shared/contract'
import type { SkyState } from './types'

/** Optional source layers are percentages, not invented cloud-base measurements. */
export type SkyForecastHour = Partial<HourPoint> & {
  cloud_cover_low_pct?: number | null
  cloud_cover_mid_pct?: number | null
  cloud_cover_high_pct?: number | null
  precip_rate_mmh?: number | null
  demo?: boolean
}

export const clamp = (x: number, lo = 0, hi = 1): number => Math.min(hi, Math.max(lo, x))
export const finite = (x: number | null | undefined, fallback = 0): number => x != null && Number.isFinite(x) ? x : fallback
export const smooth = (a: number, b: number, x: number): number => { const t = clamp((x - a) / (b - a)); return t * t * (3 - 2 * t) }
/** Fast initial response survives continuous retargeting; exact endpoint has no residual snap. */
export function skyTransitionProgress(elapsed: number, duration: number): number {
  if (duration <= 0) return 1
  const t = clamp(elapsed / duration)
  return (1 - Math.exp(-6 * t)) / (1 - Math.exp(-6))
}
const fraction = (x: number | null | undefined) => x == null || !Number.isFinite(x) ? undefined : clamp(x / 100)

/** No clock, IO, global state, timezone guesses, or use of is_day for astronomy. SunCalc v2 uses DEGREES. */
export function skyStateFromForecast(hour: SkyForecastHour, place: Pick<Place, 'latitude' | 'longitude'>,
  time: number | string | Date = hour.time ?? 0): SkyState {
  const ms = typeof time === 'number' ? time : typeof time === 'string' ? Date.parse(time) : time.getTime()
  if (!Number.isFinite(ms) || !Number.isFinite(place.latitude) || !Number.isFinite(place.longitude)) throw new RangeError('Sky needs a finite instant and coordinates')
  const date = new Date(ms)
  const sun = SunCalc.getPosition(date, place.latitude, place.longitude)
  const moon = SunCalc.getMoonPosition(date, place.latitude, place.longitude)
  const light = SunCalc.getMoonIllumination(date)
  const condition = hour.condition ?? 'cloudy'
  const thunder = /thunderstorm/.test(condition)
  const snowy = /snow|flurr|blizzard/.test(condition)
  const wet = /rain|drizzle|shower|sleet|wintry|hail|tropical|hurricane/.test(condition) || thunder
  const kind = hour.precip_type
  const precipitationKind = kind === 'snow' || kind === 'hail' || kind === 'sleet' ? kind
    : kind === 'mix' || condition === 'wintry_mix' ? 'sleet' : kind === 'rain' || kind === 'freezing_rain' ? 'rain'
      : snowy ? 'snow' : condition === 'sleet' ? 'sleet' : condition === 'hail' ? 'hail' : wet ? 'rain' : null
  // A present 0 is authoritative. Missing rates get a condition-derived VISUAL proxy, never probability-as-intensity.
  const proxy = /heavy|severe|hurricane/.test(condition) ? 9 : /light|drizzle|flurr/.test(condition) ? .35 : wet || snowy ? 2 : 0
  const snowRate = hour.snow_cm == null ? undefined : hour.snow_cm // ~10:1 snow/liquid proxy: cm snow ≈ mm water.
  // Match the published foundation formatter: precipitation is visual severity 0..1 (8 mm/h saturates).
  const precipitation = clamp(finite(hour.precip_rate_mmh ?? hour.precip_mm ?? (snowy ? snowRate : undefined), proxy) / 8)
  const coverProxy = condition === 'clear' || condition === 'hot' || condition === 'frigid' ? 0
    : condition === 'mostly_clear' || condition === 'breezy' || condition === 'windy' ? .12
      : condition === 'partly_cloudy' || condition === 'scattered_showers' || condition === 'scattered_thunderstorms' ? .46
        : condition === 'mostly_cloudy' ? .76 : wet || snowy || condition === 'cloudy' || condition === 'fog' ? 1 : .25
  const visibility = hour.visibility_m == null || !Number.isFinite(hour.visibility_m) ? null : Math.max(0, hour.visibility_m)
  const fog = condition === 'fog' ? .85 : condition === 'haze' || condition === 'dust' ? .28 : condition === 'smoke' ? .55
    : visibility == null ? 0 : .65 * (1 - smooth(500, 8000, visibility))
  return {
    condition, time: ms, latitude: place.latitude, longitude: place.longitude, isDay: sun.altitude > -.833,
    cloudCover: hour.cloud_cover_pct == null ? coverProxy : clamp(finite(hour.cloud_cover_pct) / 100),
    cloudLow: fraction(hour.cloud_cover_low_pct), cloudMid: fraction(hour.cloud_cover_mid_pct), cloudHigh: fraction(hour.cloud_cover_high_pct),
    precipitation, precipitationKind, windSpeed: clamp(finite(hour.wind_ms, 3), 0, 100),
    windDirection: ((finite(hour.wind_dir_deg, 250) % 360) + 360) % 360, fog, visibility, thunder,
    sunAltitude: sun.altitude, sunAzimuth: sun.azimuth, moonAltitude: moon.altitude, moonAzimuth: moon.azimuth,
    moonIllumination: light.fraction, demo: hour.demo === true,
  }
}

/** Change ephemerides only; the host supplies/interpolates meteorology for the new cursor. */
export function skyStateAtTime(state: SkyState, ms: number): SkyState {
  if (!Number.isFinite(ms)) throw new RangeError('Sky time must be finite')
  const date = new Date(ms), sun = SunCalc.getPosition(date, state.latitude, state.longitude)
  const moon = SunCalc.getMoonPosition(date, state.latitude, state.longitude)
  return { ...state, time: ms, isDay: sun.altitude > -.833, sunAltitude: sun.altitude, sunAzimuth: sun.azimuth,
    moonAltitude: moon.altitude, moonAzimuth: moon.azimuth, moonIllumination: SunCalc.getMoonIllumination(date).fraction }
}

// Compact numeric render contract: built only on input, interpolated in preallocated arrays.
export const N = 44
export const I = { top: 0, middle: 3, bottom: 6, cloud: 9, low: 10, mid: 11, high: 12, storm: 13, fog: 14,
  rain: 15, snow: 16, windX: 17, windY: 18, sunX: 19, sunY: 20, sunVisible: 21, moonX: 22, moonY: 23,
  moonVisible: 24, moonLightX: 25, moonLightY: 26, moonLightZ: 27, night: 28, golden: 29,
  haze: 30, smoke: 31, thunder: 32, altitude: 33, cloudLight: 34, dust: 35, moonEnergy: 36, extinction: 37 } as const

const KNOTS = [-18, -10, -5, 0, 6, 18, 80]
const COLORS = [
  ['#07162e', '#163654', '#425b74'], ['#453458', '#ac667f', '#e9a58c'],
  ['#513c60', '#c77a8e', '#f0b799'], ['#304560', '#bd8894', '#f3c29c'],
  ['#235b90', '#7aaccb', '#f4d3a5'], ['#23669f', '#6fa9cd', '#bddbe9'],
  ['#1b609f', '#61a4ce', '#b5d8eb'],
].map(row => row.flatMap(hex => { const n = parseInt(hex.slice(1), 16); return [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255] }))

export function writeSkyVector(s: SkyState, v: Float32Array): void {
  const alt = finite(s.sunAltitude, -20), cover = clamp(finite(s.cloudCover)), fog = clamp(finite(s.fog))
  const storm = s.thunder ? .96 : clamp(cover - .6) * 1.35 + (s.precipitation > 0 ? .28 : 0)
  let k = 0
  while (k < KNOTS.length - 2 && alt > KNOTS[k + 1]) k++
  const blend = smooth(KNOTS[k], KNOTS[k + 1], alt), day = smooth(-12, 8, alt)
  for (let i = 0; i < 9; i++) {
    const base = COLORS[k][i] + (COLORS[k + 1][i] - COLORS[k][i]) * blend
    const cloudTint = (i < 3 ? .11 : i < 6 ? .24 : .4) * (.2 + .8 * day)
    v[i] = base + (cloudTint - base) * clamp(storm * .7 + fog * .18)
  }
  v[I.cloud] = cover
  v[I.low] = clamp(finite(s.cloudLow, cover * .92))
  v[I.mid] = clamp(finite(s.cloudMid, Math.max(0, cover - .45) * 1.6))
  v[I.high] = clamp(finite(s.cloudHigh, cover * .4))
  v[I.storm] = clamp(storm)
  v[I.fog] = fog
  const precip = clamp(finite(s.precipitation))
  // Preserve light drizzle at low rates; dry observations never acquire particles from a condition label.
  const amount = s.precipitation > 0 ? .12 + .88 * Math.sqrt(precip) : 0
  v[I.rain] = s.precipitationKind === 'rain' || s.precipitationKind === 'hail' ? amount : s.precipitationKind === 'sleet' ? amount * .65 : 0
  v[I.snow] = s.precipitationKind === 'snow' ? amount : s.precipitationKind === 'sleet' ? amount * .5 : 0
  const direction = finite(s.windDirection) * Math.PI / 180, speed = clamp(finite(s.windSpeed), 0, 60)
  v[I.windX] = -Math.sin(direction) * speed
  v[I.windY] = -Math.cos(direction) * speed
  v[I.sunX] = finite(s.sunAzimuth, 180) / 360
  v[I.sunY] = .13 + alt / 100
  v[I.sunVisible] = smooth(-1.5, .5, alt) * (1 - cover * .85) * (1 - fog * .9)
  v[I.moonX] = finite(s.moonAzimuth, 180) / 360
  // A compressed sky-dome projection keeps low lunar altitudes out of the opaque reading ground.
  // Horizon is unchanged; altitude is monotonic, with zenith at .93. This is not a compass camera.
  const lunarAltitude = clamp(finite(s.moonAltitude, -90), -90, 90)
  v[I.moonY] = .13 + Math.sign(lunarAltitude) * .8 * Math.sqrt(Math.sin(Math.abs(lunarAltitude) * Math.PI / 180))
  v[I.night] = 1 - smooth(-15, -2, alt)
  v[I.moonVisible] = s.moonAltitude == null || s.moonAzimuth == null || s.moonIllumination == null ? 0
    : smooth(-1, 3, s.moonAltitude) * (1 - smooth(-4, 1, alt)) * (1 - fog * .8)
  const date = new Date(s.time), illumination = SunCalc.getMoonIllumination(date)
  const position = SunCalc.getMoonPosition(date, s.latitude, s.longitude)
  const z = 2 * clamp(finite(s.moonIllumination)) - 1
  const limb = (illumination.angle - position.parallacticAngle) * Math.PI / 180
  const xy = Math.sqrt(Math.max(0, 1 - z * z))
  // SunCalc position angles run opposite the panorama's increasing-azimuth screen-right basis.
  v[I.moonLightX] = -Math.sin(limb) * xy
  v[I.moonLightY] = Math.cos(limb) * xy
  v[I.moonLightZ] = z
  v[I.golden] = smooth(-5, 0, alt) * (1 - smooth(5, 16, alt))
  v[I.haze] = s.condition === 'haze' ? .5 : 0
  v[I.smoke] = s.condition === 'smoke' ? .65 : 0
  v[I.dust] = s.condition === 'dust' ? .6 : 0
  v[I.thunder] = s.thunder ? 1 : 0
  v[I.altitude] = alt
  v[I.cloudLight] = day
  // Artistic airglow, not a claim that this location has measured light pollution.
  v[I.extinction] = clamp(fog * .72 + v[I.haze] * .4 + v[I.smoke] * .6 + v[I.dust] * .5)
  v[I.moonEnergy] = v[I.moonVisible] * Math.pow(clamp(finite(s.moonIllumination)), 1.4) * (1 - cover * .65)
}

/** Mutates out, safe to call every frame. Retarget from the current visible vector to avoid scrub discontinuities. */
export function blendSkyVectors(from: Float32Array, to: Float32Array, out: Float32Array, t: number): void {
  const f = clamp(t)
  for (let i = 0; i < N; i++) {
    let delta = to[i] - from[i]
    // Wrap azimuth across north along the short arc instead of crossing the entire frame.
    if ((i === I.sunX || i === I.moonX) && Math.abs(delta) > .5) delta -= Math.sign(delta)
    out[i] = from[i] + delta * f
  }
}

/** Artistic sky framing reserves clear air beside the reading; ephemerides and phase stay unchanged. */
export function composeCelestialBodies(source: Float32Array, out: Float32Array, width: number, height: number): void {
  out.set(source)
  const phone = width < 600, ultra = width >= 2400
  const right = ultra ? (width - 256) / 4 + 28 : width - (phone ? 48 : width < 1100 ? 32 : 112)
  const top = phone ? 180 : 212
  out[I.sunX] = (right + Math.sin(source[I.sunX] * Math.PI * 2) * 8) / Math.max(1, width)
  out[I.moonX] = (right + Math.sin(source[I.moonX] * Math.PI * 2) * 8) / Math.max(1, width)
  out[I.sunY] = 1 - (top - clamp(source[I.sunY]) * 12) / Math.max(1, height)
  out[I.moonY] = 1 - (top - clamp(source[I.moonY]) * 12) / Math.max(1, height)
}

/** Bounded backing store; visual coordinates always use CSS pixels, independently of adaptive sampling. */
export function skyBufferSize(width: number, height: number, dpr: number, maxPixels = 1_500_000, quality = 1) {
  const w = Math.max(1, finite(width, 1)), h = Math.max(1, finite(height, 1)), ratio = clamp(finite(dpr, 1), .5, 3)
  const scale = Math.min(ratio, Math.sqrt(maxPixels / (w * h))) * clamp(quality, .25, 1)
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)), scale }
}

/** One gentle, spatially restricted pulse, at least 12 seconds between strikes; never a frame-wide flash. */
export const LIGHTNING_INTERVAL_MS = 12_000
export function lightningEnvelope(elapsedMs: number): number {
  return elapsedMs < 0 || elapsedMs > 800 ? 0 : Math.sin(elapsedMs / 800 * Math.PI) ** 2 * .18
}
